if (!customElements.get('maxler-compact-menu')) {
  customElements.define('maxler-compact-menu', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};

      this.addEventListener('click', event => {
        const button = event.target.closest('[data-compact-toggle]');
        if (!button || !this.contains(button)) return;
        this.setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
      }, options);

      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') {
          this.handledEscape = false;
          return;
        }

        const button = [...this.querySelectorAll('[data-compact-toggle][aria-expanded="true"]')]
          .reverse()
          .find(candidate => this.panel(candidate)?.contains(event.target) || candidate === event.target);
        if (!button) return;

        this.handledEscape = true;
        event.preventDefault();
        event.stopPropagation();
        this.setExpanded(button, false, true);
      }, options);

      this.addEventListener('keyup', event => {
        if (event.key !== 'Escape') return;
        if (this.handledEscape) {
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
        this.handledEscape = false;
      }, options);

      window.addEventListener('blur', () => { this.handledEscape = false; }, options);

      if (this.dataset.mode === 'desktop') {
        document.addEventListener('click', event => {
          if (!this.contains(event.target)) this.closeAll();
        }, options);
        this.addEventListener('focusout', event => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAll();
        }, options);
        window.addEventListener('resize', () => this.closeAll(), options);
        window.addEventListener('scroll', () => this.closeAll(), {...options, passive: true});
      } else {
        document.addEventListener('drawerOpen.NavDrawer', () => {
          this.closeAll();
          this.refreshDrawerFocusTrap();
        }, options);
        document.addEventListener('click', event => {
          const closeButton = event.target.closest('#NavDrawer .js-drawer-close');
          if (closeButton || (this.drawerIsOpen() && !event.target.closest('#NavDrawer, .js-drawer-open-nav'))) this.closeAll();
        }, options);
        document.addEventListener('keyup', event => {
          if (event.key === 'Escape' && this.drawerIsOpen()) this.closeAll();
        }, options);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.handledEscape = false;
    }

    panel(button) {
      const id = button.getAttribute('aria-controls');
      return id ? this.querySelector('#' + CSS.escape(id)) : null;
    }

    drawerIsOpen() {
      const drawer = document.getElementById('NavDrawer');
      const openButton = document.querySelector('.js-drawer-open-nav[aria-expanded="true"]');
      return Boolean(
        drawer &&
        document.documentElement.classList.contains('js-drawer-open') &&
        (drawer.classList.contains('drawer--is-open') || openButton)
      );
    }

    refreshDrawerFocusTrap() {
      const drawer = document.getElementById('NavDrawer');
      const activeElement = document.activeElement;
      if (!drawer || !this.drawerIsOpen() || !window.theme?.a11y?.trapFocus) return;

      const root = document.documentElement;
      if (typeof root.off === 'function') {
        root.off('focusin.drawer_focus');
        root.off('focusout.drawer_focus');
        root.off('keydown.drawer_focus');
      }

      window.theme.a11y.trapFocus({
        container: drawer,
        elementToFocus: drawer.contains(activeElement) ? activeElement : drawer,
        namespace: 'drawer_focus'
      });
    }

    positionDesktopPanel(button, panel) {
      const anchor = button.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(440, Math.max(0, viewportWidth - 32));
      const left = Math.min(Math.max(16, anchor.left), Math.max(16, viewportWidth - width - 16));
      const topLimit = Math.max(16, viewportHeight - 160);
      const top = Math.min(Math.max(16, anchor.bottom + 4), topLimit);

      panel.style.setProperty('--compact-panel-left', `${left}px`);
      panel.style.setProperty('--compact-panel-top', `${top}px`);
      panel.style.setProperty('--compact-panel-width', `${width}px`);
      panel.style.setProperty('--compact-panel-max-height', `${Math.max(0, viewportHeight - top - 16)}px`);
    }

    setExpanded(button, expanded, returnFocus = false) {
      const panel = this.panel(button);
      if (!panel) return;

      if (expanded && button.hasAttribute('data-compact-exclusive')) {
        this.querySelectorAll('[data-compact-exclusive][aria-expanded="true"]')
          .forEach(other => { if (other !== button) this.setExpanded(other, false); });
      }

      if (!expanded && (returnFocus || panel.contains(document.activeElement))) button.focus();
      if (expanded && this.dataset.mode === 'desktop' && button.classList.contains('maxler-compact-shop-toggle')) {
        this.positionDesktopPanel(button, panel);
      }

      button.setAttribute('aria-expanded', String(expanded));
      panel.hidden = !expanded;

      if (!expanded) {
        panel.querySelectorAll('[data-compact-toggle][aria-expanded="true"]')
          .forEach(child => this.setExpanded(child, false));
      }

      if (this.dataset.mode === 'mobile') this.refreshDrawerFocusTrap();
    }

    closeAll() {
      this.querySelectorAll('[data-compact-toggle][aria-expanded="true"]')
        .forEach(button => this.setExpanded(button, false));
    }
  });
}
